export enum FileManagerModule {
    NONE = 0,
    PROJECT = 1,
    MUNICIPALITY = 2,
    PERMIT = 3,
    INSPECTION_CALL = 4,
    TASK = 5,
    TASK_NOTE = 6,
    TASK_TEMPLATE = 7
}

export const FILEMANAGER_MODULE_API_MAP = {
    [FileManagerModule.PROJECT]: 'Project/ProjectFileManager',
    [FileManagerModule.MUNICIPALITY]: 'CityManagement/Municipality/MunicipalityFileManager',
    [FileManagerModule.PERMIT]: 'Project/ProjectWorkspace/PermitFileManager',
    [FileManagerModule.INSPECTION_CALL]: 'Contractor/Project/InspectionCallFileManager',
    [FileManagerModule.TASK]: 'Task',
    [FileManagerModule.TASK_NOTE]: 'TaskNote',
    [FileManagerModule.TASK_TEMPLATE]: 'TaskTemplate'
} as const;

export interface CommonFileManagerProps {
    referenceID: number;
    module?: FileManagerModule;
    height?: string | number;
    width?: string | number;
    featureID?: number;
    readonly?: boolean;
    externalUploading?: boolean;
    addToAllKey?: 'Projects' | 'Municipalities'
}

// ==============================|| Side Panel Directory INTERFACES ||============================== //

export interface DirectoryNode {
    id: string;
    name: string;
    children?: DirectoryNode[];
}

export interface SidePanelDirectoryTreeViewProps {
    open?: boolean;
    onClose?: () => void;
    data?: DirectoryNode;
    selectedDirectoryId?: string;
    onDirectorySelect?: (node: DirectoryNode) => void;
    onDirectoryDoubleClick?: (node: DirectoryNode) => void;
    onNewDirectoryClick?: (parentDirectory: DirectoryNode) => void;
    onUploadClick?: (directory: DirectoryNode) => void;
    onRenameClick?: (directory: DirectoryNode) => void;
    onDeleteClick?: (directory: DirectoryNode) => void;
    onMoveToClick?: (directory: DirectoryNode) => void;
    container?: HTMLDivElement | null;
}


// ==============================|| File and Folder Listing INTERFACES ||============================== //

export interface TabularFileItem {
    id: string;
    name: string;
    type: 'file' | 'directory';
    modifiedDate: string;
    size: string;
    modifiedBy?: string | null;
    userType?: number | null;
    status?: 'queued' | 'uploading' | 'completed' | 'failed';
    progress?: number;
    directoryPath?: string;
    originalName?: string;
    originalFullPath?: string;
    isDeleted?: boolean;
    fileID?: string | number;
    url?: string;
}

// ==============================|| Directory Map Node INTERFACE ||============================== //

export interface DirectoryMapNode {
    name: string;
    parentId: string | null;
    modifiedDate?: string;
    modifiedBy?: string;
    userType?: number | null;
}


// ==============================|| Create Directory Form INTERFACES ||============================== //


export interface DirectoryOption {
    id: string;
    name: string;
}

export interface CreateDirectoryFormProps {
    open?: boolean;
    onClose?: () => void;
    onSubmit?: (parentId: string, folderName: string) => void;
    directories?: DirectoryOption[];
    defaultParentId?: string;
}

export interface CreateDirectoryFormValues {
    parentId: string;
    folderName: string;
}

// ==============================|| Rename Directory Form INTERFACES ||============================== //

export interface RenameDirectoryFormProps {
    open?: boolean;
    onClose?: () => void;
    onSubmit?: (directoryId: string, newName: string) => void;
    directoryId?: string;
    directoryName?: string;
    currentPath?: string[];
}

export interface RenameDirectoryFormValues {
    folderName: string;
}

// ==============================|| Rename File Form INTERFACES ||============================== //


export interface RenameFileFormProps {
    open?: boolean;
    onClose?: () => void;
    onSubmit?: (fileId: string, newName: string) => void;
    fileId?: string;
    fileName?: string;
    parentPath?: string[];
}

export interface RenameFileFormValues {
    newFileName: string;
}
// ==============================|| Delete Directory and Files Dialog INTERFACES ||============================== //

export interface DeleteDirectoryAndFilesDialogProps {
    open?: boolean;
    onClose?: () => void;
    onConfirm?: () => void;
    title?: string;          // e.g. "Delete Folder?"
    subtitle?: string;       // e.g. "Are you sure you want to delete this folder? All folders..."
    itemName?: string;       // e.g. "Mixed Assets"
    itemDescription?: string;// e.g. "12 files and 3 Folders will be deleted"
    itemType?: 'folder' | 'file' | 'mixed';
    showCard?: boolean;
}

export interface DeleteFileDialogProps {
    open?: boolean;
    onClose?: () => void;
    onConfirm?: () => void;
    fileName?: string;       // e.g. "ANI-2023903888.jpg"
    filePath?: string;       // e.g. "Files > Photos > My photo > Rashmi > Festival"
    isMultiple?: boolean;
    filesCount?: number;     // e.g. 12
}


// ==============================|| Move Directory and Files Form INTERFACES ||============================== //
export interface MoveDirectoryAndFilesFormProps {
    open?: boolean;
    onClose?: () => void;
    onSubmit?: (targetDirectoryId: string) => Promise<boolean>;
    directoryTree?: DirectoryNode;
    movingIds?: string[];
    directoryMap?: Record<string, { name: string; parentId: string | null }>;
}

// ==============================|| Image Editor INTERFACES ||============================== //

export interface ImageEditorProps {
    open?: boolean;
    onClose?: () => void;
    imageFile?: File | null;
    imageUrl?: string | null;
    imageName?: string;
    onSave?: (savedImageData: any, fileName: string, isSaveAsNew?: boolean) => Promise<void> | void;
}

// ==============================|| Image Preview INTERFACES ||============================== //

export interface PreviewImageItem extends TabularFileItem {
    url: string;
}

export interface ImagePreviewProps {
    images?: PreviewImageItem[];
    currentIndex?: number;
    isOpen?: boolean;
    onClose?: () => void;
    onIndexChange?: (index: number) => void;
    onRename?: (file: TabularFileItem) => void;
    onDownload?: (file: TabularFileItem) => void;
    allowEdit?: boolean;
}

// ==============================|| PDF Preview INTERFACES ||============================== //

export interface PdfPreviewProps {
    pdfUrl: string | null;
    pdfTitle: string;
    onClose: () => void;
    onDownload?: () => void;
}




// ==============================|| Select Directory Form INTERFACES ||============================== //

export interface SelectDirectoryFormProps {
    directories: string[];
    closeModal: () => void;
    onDirectorySelected: (directoryPath: string) => void;
    submitLabel?: string;
}



// ==============================|| Update File Notes Form INTERFACES ||============================== //

export interface UpdateFileNotesformProps {
    open?: boolean;
    onClose?: () => void;
    onSubmit?: (fileId: string, note: string) => void;
    fileId?: string;
    fileName?: string;
    initialNote?: string;
}

export interface UpdateFileNotesformValues {
    note: string;
}

// ==============================|| File and Folder Listing INTERFACES ||============================== //

export interface TabularFileItem {
    id: string;
    name: string;
    type: 'directory' | 'file';
    modifiedDate: string;
    size: string;
    modifiedBy?: string | null;
    note?: string;
    userType?: number | null;
    status?: 'queued' | 'uploading' | 'completed' | 'failed';
    progress?: number;
    directoryPath?: string;
    originalName?: string;
    originalFullPath?: string;
    isDeleted?: boolean;
    fileID?: string | number;
    url?: string;
}

export interface DirectoryAndFilesListingProps {
    isLoading?: boolean;
    loadingText?: string;
    currentPath?: string[];
    onBreadcrumbClick?: (index: number) => void;
    onBackClick?: () => void;
    items?: TabularFileItem[];
    onMenuToggle?: () => void;
    onUploadClick?: () => void;
    onNewDirectoryClick?: () => void;
    onEditDirectory?: (item: TabularFileItem) => void;
    onDeleteDirectory?: (item: TabularFileItem) => void;
    onEditFile?: (item: TabularFileItem) => void;
    onRenameFile?: (item: TabularFileItem) => void;
    onDownloadFile?: (item: TabularFileItem) => void;
    onDeleteFile?: (item: TabularFileItem) => void;
    onAddNote?: (item: TabularFileItem) => void;
    onSaveNote?: (fileId: string, note: string) => void;
    onDirectoryClick?: (item: TabularFileItem) => void;
    onFileClick?: (item: TabularFileItem) => void;
    viewPDF?: boolean;
    onDeleteSelectedClick?: (selectedIds: string[]) => void;
    onDownloadSelectedClick?: (selectedIds: string[]) => void;
    onRenameSelectedClick?: (selectedIds: string[]) => void;
    onMoveToSelectedClick?: (selectedIds: string[]) => void;
}

// ==============================|| Common File Manager Raw File Item INTERFACE ||============================== //

export interface RawFileItem {
    name: string;
    id?: string;
    fileID?: string;
    fileId?: string;
    modifiedOn: string;
    size: number;
    contentType: string;
    notes?: string | null;
    markForRevisionRequest?: boolean;
    sortOrder?: number | null;
    uploadedByUserName?: string | null;
    userType?: number | null;
}

// ==============================|| Common File Manager INTERFACES ||============================== //
